import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Grid, Container, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { motion, AnimatePresence } from 'framer-motion';
import '@fontsource/poppins/200.css';

// Pricing plan data
const pricingPlans = [
  {
    title: "Basic",
    price: "€799",
    description: "Great for small teams needing a simple Kubernetes setup with monitoring and upgrades",
    features: [
      "Basic Kubernetes Infrastructure using AWS/GCP/Azure.",
      "Monitoring & Alerting",
      "Regular Upgrades"
    ]
  },
  {
    title: "Advanced",
    price: "€999",
    description: "For teams that need separate environments, basic CI-CD, and support.",
    features: [
      "Kubernetes infrastructure with separate environments for Development, Production, and Deployment.",
      "Basic CI-CD Setup",
      "Regular Upgrades",
      "Up to 5 hrs of Support"
    ]
  },
  {
    title: "Professional",
    price: "€1999",
    description: "Best for enterprises needing advanced CI-CD, multiple clusters, and high security.",
    features: [
      "Kubernetes Infrastructure with 3 clusters",
      "Advanced CI-CD Setup with Security",
      "Up to 16 hrs Support",
      "Tightened Security"
    ]
  },
  {
    title: "",
    price: "Custom",
    description: "Custom-built solutions to match your exact infrastructure needs.",
    features: [
      "Custom-built solutions to match your exact infrastructure needs",
      "Advanced CI-CD Setup with Security",
      "Kubernetes Infrastructure with 3+ Clusters",
      "Tightened Security"
    ],
    isCustom: true
  }
];

// Collapsible pricing card component
const CollapsiblePricingCard = ({ plan, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box
      sx={{
        height: 'auto',
        borderRadius: '16px',
        background: { xs: '#fad32e', md: '#262626' },
        p: 3,
        color: { xs: '#000000', md: 'white' },
        width: { xs: '100%', md: '340px' },
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Header with title and toggle button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 'medium',
          fontFamily: 'Poppins, sans-serif',
          color: { xs: '#000000', md: '#ffffff' }
        }}>
          {plan.title}
        </Typography>
        
        {isMobile && (
          <IconButton 
            onClick={toggleExpand}
            sx={{ 
              color: '#000000',
              p: 0
            }}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        )}
      </Box>
      
      {/* Price */}
      <Typography 
        variant="h3" 
        sx={{ 
          fontWeight: 'regular',
          fontFamily: 'Poppins, sans-serif', 
          mb: 1,
          fontSize: { xs: '2.5rem', md: '3rem' },
          color: { xs: '#000000', md: '#ffffff' }
        }}
      >
        {plan.price}
        {plan.price !== "Custom" && (
          <Typography component="span" variant="body1" sx={{ 
            fontSize: '1rem', 
            ml: 0.5,
            color: { xs: '#000000', md: '#ffffff' }
          }}>
            /month
          </Typography>
        )}
      </Typography>
      
      {/* Description - always visible */}
      <Typography variant="body2" sx={{ 
        mb: 4, 
        minHeight: { xs: 'auto', md: '80px' }, 
        fontSize: '0.9rem',
        fontWeight: 200,
        fontFamily: 'Poppins, sans-serif',
        color: { xs: '#000000', md: '#ffffff' }
      }}>
        {plan.description}
      </Typography>
      
      {/* Features section - collapsible on mobile */}
      <AnimatePresence>
        {(!isMobile || isExpanded) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            {/* Features divider */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 3
              }}
            >
              <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(0,0,0,0.2)' }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  px: 2, 
                  color: { xs: 'rgba(0,0,0,0.6)', md: 'rgba(255,255,255,0.6)' },
                  fontSize: '0.75rem',
                  letterSpacing: '1px'
                }}
              >
                FEATURES
              </Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: { xs: 'rgba(0,0,0,0.2)', md: 'rgba(255,255,255,0.2)' } }} />
            </Box>
            
            {/* Features list */}
            <Box sx={{ flexGrow: 1 }}>
              {plan.features.map((feature, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <CheckCircleIcon sx={{ 
                    color: { xs: '#000000', md: '#fad32e' }, 
                    mr: 1.5, 
                    fontSize: '1rem', 
                    mt: 0.3 
                  }} />
                  <Typography variant="body2" sx={{ 
                    fontSize: '0.8rem',
                    fontFamily: 'Poppins, sans-serif',
                    color: { xs: '#000000', md: '#ffffff' }
                  }}>
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>
            
            {/* Custom plan button - only shown for custom plan */}
            {plan.isCustom && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center',
                alignItems: 'center',
                mt: 3
              }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: { xs: '#000000', md: '#fad32e' },
                    color: { xs: '#fad32e', md: '#000000' },
                    py: 1,
                    px: 4,
                    width: '100%',
                    borderRadius: '24px',
                    '&:hover': {
                      bgcolor: { xs: '#333333', md: '#e0bd29' },
                    },
                    fontWeight: 800,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '0.9rem'
                  }}
                  onClick={() => {
                    window.open("https://calendly.com/clockhash/talk-to-us", "_blank");
                }}
                >
                  Talk to Sales
                </Button>
              </Box>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

const PricingSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Box
      sx={{
        bgcolor: '#000000',
        py: 10,
        px: { xs: 2, md: 4 }
      }}
    >
      <Container maxWidth="xl">
        {/* Main Heading */}
        <Box sx={{ mb: 12, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              color: '#fad32e',
              fontWeight: 'regular',
              fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
              lineHeight: 1.2,
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Simple and Affordable
          </Typography>
          <Typography
            variant="h2"
            sx={{
              color: '#fad32e',
              fontWeight: 'regular',
              fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
              lineHeight: 1.2,
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Pricing Plan
          </Typography>
          <Typography
            variant="h2"
            sx={{
              color: '#FFFFFF',
              fontWeight: 200,
              fontSize: { xs: '0.7rem', sm: '1rem', md: '1.5rem' },
              lineHeight: 1.2,
              fontFamily: 'Poppins, sans-serif',
              mt: 2,
            }}
          >
            Start Tracking and Improving Your Project
          </Typography>
        </Box>

        {/* Pricing Cards */}
        <Grid 
          container 
          spacing={isMobile ? 2 : 3} 
          justifyContent='center' 
          alignItems='stretch'
        >
          {pricingPlans.map((plan, index) => (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              lg={3} 
              key={index}
              sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mb: { xs: 2, md: 0 }
              }}
            >
              <CollapsiblePricingCard plan={plan} index={index} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PricingSection;





// import React, { useState, useRef, useEffect } from 'react';
// import { Box, Typography, Grid, Container, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
// import { motion, AnimatePresence } from 'framer-motion';
// import '@fontsource/poppins/200.css';

// // Pricing plan data
// const pricingPlans = [
//   {
//     title: "Basic",
//     price: "€799",
//     description: "Great for small teams needing a simple Kubernetes setup with monitoring and upgrades",
//     features: [
//       "Basic Kubernetes Infrastructure using AWS/GCP/Azure.",
//       "Monitoring & Alerting",
//       "Regular Upgrades"
//     ]
//   },
//   {
//     title: "Advanced",
//     price: "€999",
//     description: "For teams that need separate environments, basic CI-CD, and support.",
//     features: [
//       "Kubernetes infrastructure with separate environments for Development, Production, and Deployment.",
//       "Basic CI-CD Setup",
//       "Regular Upgrades",
//       "Up to 5 hrs of Support"
//     ]
//   },
//   {
//     title: "Professional",
//     price: "€1999",
//     description: "Best for enterprises needing advanced CI-CD, multiple clusters, and high security.",
//     features: [
//       "Kubernetes Infrastructure with 3 clusters",
//       "Advanced CI-CD Setup with Security",
//       "Up to 16 hrs Support",
//       "Tightened Security"
//     ]
//   },
//   {
//     title: "Custom",
//     price: "Custom",
//     description: "Custom-built solutions to match your exact infrastructure needs.",
//     features: [
//       "Custom-built solutions to match your exact infrastructure needs",
//       "Advanced CI-CD Setup with Security",
//       "Kubernetes Infrastructure with 3+ Clusters",
//       "Tightened Security"
//     ],
//     isCustom: true
//   }
// ];

// // Collapsible pricing card component
// const CollapsiblePricingCard = ({ plan, index }) => {
//   const [isExpanded, setIsExpanded] = useState(false);
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
//   const toggleExpand = () => {
//     setIsExpanded(!isExpanded);
//   };

//   return (
//     <Box
//       sx={{
//         height: 'auto',
//         borderRadius: '16px',
//         background: { xs: '#fad32e', md: '#262626' },
//         p: 3,
//         color: { xs: '#000000', md: 'white' },
//         width: { xs: '100%', md: '286px' },
//         display: 'flex',
//         flexDirection: 'column',
//         transition: 'all 0.3s ease',
//         boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
//         position: 'relative',
//         overflow: 'hidden',
//       }}
//     >
//       {/* Header with title and toggle button */}
//       <Box sx={{ 
//         display: 'flex', 
//         justifyContent: 'space-between', 
//         alignItems: 'center',
//         mb: 2
//       }}>
//         <Typography variant="h5" sx={{ 
//           fontWeight: 'medium',
//           fontFamily: 'Poppins, sans-serif',
//           color: { xs: '#000000', md: '#ffffff' }
//         }}>
//           {plan.title}
//         </Typography>
        
//         {isMobile && (
//           <IconButton 
//             onClick={toggleExpand}
//             sx={{ 
//               color: '#000000',
//               p: 0
//             }}
//           >
//             {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
//           </IconButton>
//         )}
//       </Box>
      
//       {/* Price */}
//       <Typography 
//         variant="h3" 
//         sx={{ 
//           fontWeight: 'regular',
//           fontFamily: 'Poppins, sans-serif', 
//           mb: 1,
//           fontSize: { xs: '2.5rem', md: '3rem' },
//           color: { xs: '#000000', md: '#ffffff' }
//         }}
//       >
//         {plan.price}
//         {plan.price !== "Custom" && (
//           <Typography component="span" variant="body1" sx={{ 
//             fontSize: '1rem', 
//             ml: 0.5,
//             color: { xs: '#000000', md: '#ffffff' }
//           }}>
//             /month
//           </Typography>
//         )}
//       </Typography>
      
//       {/* Description - always visible */}
//       <Typography variant="body2" sx={{ 
//         mb: 4, 
//         minHeight: { xs: 'auto', md: '80px' }, 
//         fontSize: '0.9rem',
//         fontWeight: 200,
//         fontFamily: 'Poppins, sans-serif',
//         color: { xs: '#000000', md: '#ffffff' }
//       }}>
//         {plan.description}
//       </Typography>
      
//       {/* Features section - collapsible on mobile */}
//       <AnimatePresence>
//         {(!isMobile || isExpanded) && !plan.isCustom && (
//           <motion.div
//             initial={{ height: 0, opacity: 0 }}
//             animate={{ height: 'auto', opacity: 1 }}
//             exit={{ height: 0, opacity: 0 }}
//             transition={{ duration: 0.3 }}
//             style={{ overflow: 'hidden' }}
//           >
//             {/* Features divider */}
//             <Box 
//               sx={{ 
//                 display: 'flex', 
//                 alignItems: 'center', 
//                 justifyContent: 'center',
//                 mb: 3
//               }}
//             >
//               <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(0,0,0,0.2)' }} />
//               <Typography 
//                 variant="caption" 
//                 sx={{ 
//                   px: 2, 
//                   color: { xs: 'rgba(0,0,0,0.6)', md: 'rgba(255,255,255,0.6)' },
//                   fontSize: '0.75rem',
//                   letterSpacing: '1px'
//                 }}
//               >
//                 FEATURES
//               </Typography>
//               <Box sx={{ flex: 1, height: '1px', bgcolor: { xs: 'rgba(0,0,0,0.2)', md: 'rgba(255,255,255,0.2)' } }} />
//             </Box>
            
//             {/* Features list */}
//             <Box sx={{ flexGrow: 1 }}>
//               {plan.features.map((feature, idx) => (
//                 <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
//                   <CheckCircleIcon sx={{ 
//                     color: { xs: '#000000', md: '#fad32e' }, 
//                     mr: 1.5, 
//                     fontSize: '1rem', 
//                     mt: 0.3 
//                   }} />
//                   <Typography variant="body2" sx={{ 
//                     fontSize: '0.8rem',
//                     fontFamily: 'Poppins, sans-serif',
//                     color: { xs: '#000000', md: '#ffffff' }
//                   }}>
//                     {feature}
//                   </Typography>
//                 </Box>
//               ))}
//             </Box>
//           </motion.div>
//         )}
//       </AnimatePresence>
      
//       {/* Custom plan button */}
//       {plan.isCustom && (
//         <Box sx={{ 
//           flexGrow: 1, 
//           display: 'flex', 
//           flexDirection: 'column', 
//           justifyContent: 'center',
//           alignItems: 'center',
//           mt: 2
//         }}>
//           <Button
//             variant="contained"
//             sx={{
//               bgcolor: { xs: '#000000', md: '#fad32e' },
//               color: { xs: '#fad32e', md: '#000000' },
//               py: 1,
//               px: 4,
//               width: '100%',
//               borderRadius: '24px',
//               '&:hover': {
//                 bgcolor: { xs: '#333333', md: '#e0bd29' },
//               },
//               fontWeight: 800,
//               fontFamily: 'Poppins, sans-serif',
//               fontSize: '0.9rem'
//             }}
//           >
//             Talk to Sales
//           </Button>
//         </Box>
//       )}
//     </Box>
//   );
// };

// const PricingSection = () => {
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
//   return (
//     <Box
//       sx={{
//         bgcolor: '#000000',
//         py: 10,
//         px: { xs: 2, md: 4 }
//       }}
//     >
//       <Container maxWidth="xl">
//         {/* Main Heading */}
//         <Box sx={{ mb: 12, textAlign: 'center' }}>
//           <Typography
//             variant="h2"
//             sx={{
//               color: '#fad32e',
//               fontWeight: 'regular',
//               fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
//               lineHeight: 1.2,
//               fontFamily: 'Poppins, sans-serif',
//             }}
//           >
//             Simple and Affordable
//           </Typography>
//           <Typography
//             variant="h2"
//             sx={{
//               color: '#fad32e',
//               fontWeight: 'regular',
//               fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
//               lineHeight: 1.2,
//               fontFamily: 'Poppins, sans-serif',
//             }}
//           >
//             Pricing Plan
//           </Typography>
//           <Typography
//             variant="h2"
//             sx={{
//               color: '#FFFFFF',
//               fontWeight: 200,
//               fontSize: { xs: '0.7rem', sm: '1rem', md: '1.5rem' },
//               lineHeight: 1.2,
//               fontFamily: 'Poppins, sans-serif',
//               mt: 2,
//             }}
//           >
//             Start Tracking and Improving Your Project
//           </Typography>
//         </Box>

//         {/* Pricing Cards */}
//         <Grid 
//           container 
//           spacing={isMobile ? 2 : 3} 
//           justifyContent='center' 
//           alignItems='stretch'
//         >
//           {pricingPlans.map((plan, index) => (
//             <Grid 
//               item 
//               xs={12} 
//               sm={6} 
//               lg={3} 
//               key={index}
//               sx={{ 
//                 display: 'flex', 
//                 justifyContent: 'center',
//                 mb: { xs: 2, md: 0 }
//               }}
//             >
//               <CollapsiblePricingCard plan={plan} index={index} />
//             </Grid>
//           ))}
//         </Grid>
//       </Container>
//     </Box>
//   );
// };

// export default PricingSection;









// import React, { useRef, useState, useEffect } from 'react';
// import { Box, Typography, Grid, Container, Button } from '@mui/material';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import '@fontsource/poppins/200.css';
// import { motion } from 'framer-motion';

// // Animated pricing card component
// const AnimatedPricingCard = ({ children, index }) => {
//   const [isVisible, setIsVisible] = useState(false);
//   const cardRef = useRef(null);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting) {
//           setIsVisible(true);
//           observer.unobserve(entry.target);
//         }
//       },
//       { threshold: 0.2 } // Trigger when 20% of the card is visible
//     );

//     if (cardRef.current) {
//       observer.observe(cardRef.current);
//     }

//     return () => {
//       if (cardRef.current) {
//         observer.unobserve(cardRef.current);
//       }
//     };
//   }, []);

//   return (
//     <motion.div
//       ref={cardRef}
//       initial={{ opacity: 0, y: 50 }}
//       animate={{ 
//         opacity: isVisible ? 1 : 0, 
//         y: isVisible ? 0 : 50,
//       }}
//       transition={{ 
//         duration: 0.7, 
//         delay: 0.2 + (index * 0.15), // Staggered delay based on card index
//         ease: "easeOut"
//       }}
//       style={{ height: '100%', display: 'flex', justifyContent: 'center' }}
//     >
//       {children}
//     </motion.div>
//   );
// };

// // Animated feature item component
// const AnimatedFeatureItem = ({ icon, text, delay }) => {
//   const [isVisible, setIsVisible] = useState(false);
//   const featureRef = useRef(null);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting) {
//           setIsVisible(true);
//           observer.unobserve(entry.target);
//         }
//       },
//       { threshold: 0.5 }
//     );

//     if (featureRef.current) {
//       observer.observe(featureRef.current);
//     }

//     return () => {
//       if (featureRef.current) {
//         observer.unobserve(featureRef.current);
//       }
//     };
//   }, []);

//   return (
//     <motion.div
//       ref={featureRef}
//       initial={{ opacity: 0, x: -20 }}
//       animate={{ 
//         opacity: isVisible ? 1 : 0, 
//         x: isVisible ? 0 : -20 
//       }}
//       transition={{ 
//         duration: 0.5, 
//         delay: delay,
//       }}
//     >
//       <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
//         <motion.div
//           initial={{ scale: 0 }}
//           animate={{ scale: isVisible ? 1 : 0 }}
//           transition={{ duration: 0.3, delay: delay + 0.1 }}
//         >
//           {icon}
//         </motion.div>
//         <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: 'Poppins, sans-serif' }}>
//           {text}
//         </Typography>
//       </Box>
//     </motion.div>
//   );
// };

// const PricingSection = () => {
//   const [headingVisible, setHeadingVisible] = useState(false);
//   const headingRef = useRef(null);

//   useEffect(() => {
//     const observer = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting) {
//           setHeadingVisible(true);
//           observer.unobserve(entry.target);
//         }
//       },
//       { threshold: 0.1 }
//     );

//     if (headingRef.current) {
//       observer.observe(headingRef.current);
//     }

//     return () => {
//       if (headingRef.current) {
//         observer.unobserve(headingRef.current);
//       }
//     };
//   }, []);

//   return (
//     <Box
//       sx={{
//         bgcolor: '#000000',
//         py: 10,
//         px: { xs: 2, md: 4 }
//       }}
//     >
//       <Container maxWidth="xl">
//         {/* Main Heading with animation */}
//         <Box sx={{ mb: 12, textAlign: 'center' }} ref={headingRef}>
//           <motion.div
//             initial={{ opacity: 0, y: -30 }}
//             animate={{ 
//               opacity: headingVisible ? 1 : 0, 
//               y: headingVisible ? 0 : -30 
//             }}
//             transition={{ duration: 0.7 }}
//           >
//             <Typography
//               variant="h2"
//               sx={{
//                 color: '#fad32e',
//                 fontWeight: 'regular',
//                 fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
//                 lineHeight: 1.2,
//                 fontFamily: 'Poppins, sans-serif',
//               }}
//             >
//               Simple and Affordable
//             </Typography>
//           </motion.div>
          
//           <motion.div
//             initial={{ opacity: 0, y: -30 }}
//             animate={{ 
//               opacity: headingVisible ? 1 : 0, 
//               y: headingVisible ? 0 : -30 
//             }}
//             transition={{ duration: 0.7, delay: 0.2 }}
//           >
//             <Typography
//               variant="h2"
//               sx={{
//                 color: '#fad32e',
//                 fontWeight: 'regular',
//                 fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
//                 lineHeight: 1.2,
//                 fontFamily: 'Poppins, sans-serif',
//               }}
//             >
//               Pricing Plan
//             </Typography>
//           </motion.div>
          
//           <motion.div
//             initial={{ opacity: 0, y: -20 }}
//             animate={{ 
//               opacity: headingVisible ? 1 : 0, 
//               y: headingVisible ? 0 : -20 
//             }}
//             transition={{ duration: 0.7, delay: 0.4 }}
//           >
//             <Typography
//               variant="h2"
//               sx={{
//                 color: '#FFFFFF',
//                 fontWeight: 200,
//                 fontSize: { xs: '0.7rem', sm: '1rem', md: '1.5rem' },
//                 lineHeight: 1.2,
//                 fontFamily: 'Poppins, sans-serif',
//                 mt: 2,
//               }}
//             >
//               Start Tracking and Improving Your Project
//             </Typography>
//           </motion.div>
//         </Box>

//         {/* Pricing Cards */}
//         <Grid container spacing={2} justifyContent='center' alignItems='center'>
//           {/* Basic Plan */}
//           <Grid item xs={12} sm={6} lg={3} sx={{ display: 'flex', justifyContent: 'center' }}>
//             <AnimatedPricingCard index={0}>
//               <Box
//                 sx={{
//                   height: { xs: '100%', md: '400px' },
//                   borderRadius: '16px',
//                   background: '#262626',
//                   p: 3,
//                   color: 'white',
//                   width:'286px',
//                   display: 'flex',
//                   flexDirection: 'column',
//                   transition: 'transform 0.3s ease, box-shadow 0.3s ease',
//                   '&:hover': {
//                     transform: 'translateY(-10px)',
//                     boxShadow: '0 10px 30px rgba(255, 255, 255, 0.2)',
//                   },
//                 }}
//               >
//                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 'light', fontFamily: 'Poppins, sans-serif' }}>
//                   Basic
//                 </Typography>
                
//                 <Typography 
//                   variant="h3" 
//                   sx={{ 
//                     fontWeight: 'regular',
//                     fontFamily: 'Poppins, sans-serif', 
//                     mb: 1,
//                     fontSize: { xs: '2.5rem', md: '3rem' }
//                   }}
//                 >
//                   €799
//                   <Typography component="span" variant="body1" sx={{ fontSize: '1rem', ml: 0.5 }}>
//                     /month
//                   </Typography>
//                 </Typography>
                
//                 <Typography variant="body2" sx={{ mb: 4, minHeight: '80px', fontSize: '0.9rem', fontWeight: 200, fontFamily: 'Poppins, sans-serif' }}>
//                   Great for small teams needing a simple Kubernetes setup with monitoring and upgrades
//                 </Typography>
                
//                 <Box 
//                   sx={{ 
//                     display: 'flex', 
//                     alignItems: 'center', 
//                     justifyContent: 'center',
//                     mb: 3
//                   }}
//                 >
//                   <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.2)' }} />
//                   <Typography 
//                     variant="caption" 
//                     sx={{ 
//                       px: 2, 
//                       color: 'rgba(255,255,255,0.6)',
//                       fontSize: '0.75rem',
//                       letterSpacing: '1px'
//                     }}
//                   >
//                     FEATURES
//                   </Typography>
//                   <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.2)' }} />
//                 </Box>
                
//                 <Box sx={{ flexGrow: 1 }}>
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Basic Kubernetes Infrastructure using AWS/GCP/Azure."
//                     delay={0.6}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Monitoring & Alerting"
//                     delay={0.7}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Regular Upgrades"
//                     delay={0.8}
//                   />
//                 </Box>
//               </Box>
//             </AnimatedPricingCard>
//           </Grid>

//           {/* Advanced Plan */}
//           <Grid item xs={12} sm={6} lg={3}>
//             <AnimatedPricingCard index={1}>
//               <Box
//                 sx={{
//                   height: { xs: '100%', md: '400px' },
//                   borderRadius: '16px',
//                   background: '#262626',
//                   p: 3,
//                   color: 'white',
//                   display: 'flex',
//                   flexDirection: 'column',
//                   width:'286px',
//                   transition: 'transform 0.3s ease, box-shadow 0.3s ease',
//                   '&:hover': {
//                     transform: 'translateY(-10px)',
//                     boxShadow: '0 10px 30px rgba(255, 255, 255, 0.2)',
//                   },
//                 }}
//               >
//                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 'normal' }}>
//                   Advanced
//                 </Typography>
                
//                 <Typography 
//                   variant="h3" 
//                   sx={{ 
//                     fontWeight: 'regular',
//                     fontFamily: 'Poppins, sans-serif', 
//                     mb: 1,
//                     fontSize: { xs: '2.5rem', md: '3rem' }
//                   }}
//                 >
//                   €999
//                   <Typography component="span" variant="body1" sx={{ fontSize: '1rem', ml: 0.5 }}>
//                     /month
//                   </Typography>
//                 </Typography>
                
//                 <Typography variant="body2" sx={{ mb: 1, fontSize: '0.9rem', fontFamily: 'Poppins, sans-serif' }}>
//                   For teams that need separate environments, basic CI-CD, and support.
//                 </Typography>
                
//                 <Box 
//                   sx={{ 
//                     display: 'flex', 
//                     alignItems: 'center', 
//                     justifyContent: 'center',
//                     mb: 1
//                   }}
//                 >
//                   <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.2)' }} />
//                   <Typography 
//                     variant="caption" 
//                     sx={{ 
//                       px: 2, 
//                       color: 'rgba(255,255,255,0.6)',
//                       fontSize: '0.75rem',
//                       letterSpacing: '1px',
//                       mb: 2
//                     }}
//                   >
//                     FEATURES
//                   </Typography>
//                   <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.2)' }} />
//                 </Box>
                
//                 <Box sx={{ flexGrow: 1 }}>
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Kubernetes infrastructure with separate environments for Development, Production, and Deployment."
//                     delay={0.7}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Basic CI-CD Setup"
//                     delay={0.8}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Regular Upgrades"
//                     delay={0.9}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Up to 5 hrs of Support"
//                     delay={1.0}
//                   />
//                 </Box>
//               </Box>
//             </AnimatedPricingCard>
//           </Grid>

//           {/* Professional Plan */}
//           <Grid item xs={12} sm={6} lg={3}>
//             <AnimatedPricingCard index={2}>
//               <Box
//                 sx={{
//                   height: { xs: '100%', md: '400px' },
//                   borderRadius: '16px',
//                   background: '#262626',
//                   p: 3,
//                   color: 'white',
//                   display: 'flex',
//                   flexDirection: 'column',
//                   width:'286px',
//                   transition: 'transform 0.3s ease, box-shadow 0.3s ease',
//                   '&:hover': {
//                     transform: 'translateY(-10px)',
//                     boxShadow: '0 10px 30px rgba(255, 255, 255, 0.2)',
//                   },
//                 }}
//               >
//                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 'normal' }}>
//                   Professional
//                 </Typography>
                
//                 <Typography 
//                   variant="h3" 
//                   sx={{ 
//                     fontWeight: 'regular',
//                     fontFamily: 'Poppins, sans-serif', 
//                     mb: 1,
//                     fontSize: { xs: '2.5rem', md: '3rem' }
//                   }}
//                 >
//                   €1999
//                   <Typography component="span" variant="body1" sx={{ fontSize: '1rem', ml: 0.5 }}>
//                     /month
//                   </Typography>
//                 </Typography>
                
//                 <Typography variant="body2" sx={{ mb: 4, minHeight: '80px', fontSize: '0.9rem', fontFamily: 'Poppins, sans-serif' }}>
//                   Best for enterprises needing advanced CI-CD, multiple clusters, and high security.
//                 </Typography>
                
//                 <Box 
//                   sx={{ 
//                     display: 'flex', 
//                     alignItems: 'center', 
//                     justifyContent: 'center',
//                     mb: 3
//                   }}
//                 >
//                   <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.2)' }} />
//                   <Typography 
//                     variant="caption" 
//                     sx={{ 
//                       px: 2, 
//                       color: 'rgba(255,255,255,0.6)',
//                       fontSize: '0.75rem',
//                       letterSpacing: '1px'
//                     }}
//                   >
//                     FEATURES
//                   </Typography>
//                   <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.2)' }} />
//                 </Box>
                
//                 <Box sx={{ flexGrow: 1 }}>
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Kubernetes Infrastructure with 3 clusters"
//                     delay={0.8}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Advanced CI-CD Setup with Security"
//                     delay={0.9}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Up to 16 hrs Support"
//                     delay={1.0}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Tightened Security"
//                     delay={1.1}
//                   />
//                 </Box>
//               </Box>
//             </AnimatedPricingCard>
//           </Grid>

//           {/* Custom Plan */}
//           <Grid item xs={12} sm={6} lg={3}>
//             <AnimatedPricingCard index={3}>
//               <Box
//                 sx={{
//                   height: { xs: '100%', md: '400px' },
//                   borderRadius: '16px',
//                   background: '#262626',
//                   p: 3,
//                   color: 'white',
//                   display: 'flex',
//                   flexDirection: 'column',
//                   width:'286px',
//                   transition: 'transform 0.3s ease, box-shadow 0.3s ease',
//                   '&:hover': {
//                     transform: 'translateY(-10px)',
//                     boxShadow: '0 10px 30px rgba(255, 255, 255, 0.2)',
//                   },
//                 }}
//               >
//                 <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold',fontFamily: 'Poppins, sans-serif' }}>
//                   Custom
//                 </Typography>
                
//                 <Typography variant="body2" sx={{ mb: 2, minHeight: '80px', fontSize: '0.9rem', fontFamily: 'Poppins, sans-serif' }}>
//                 Ideal for teams needing scalable deployments and enterprise-grade CI/CD.                </Typography>
                
//                 <Box 
//                   sx={{ 
//                     display: 'flex', 
//                     alignItems: 'center', 
//                     justifyContent: 'center',
//                     mb: 2
//                   }}
//                 >
//                   <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.2)' }} />
//                   <Typography 
//                     variant="caption" 
//                     sx={{ 
//                       px: 2, 
//                       color: 'rgba(255,255,255,0.6)',
//                       fontSize: '0.75rem',
//                       letterSpacing: '1px'
//                     }}
//                   >
//                     FEATURES
//                   </Typography>
//                   <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.2)' }} />
//                 </Box>
                
//                 <Box sx={{ flexGrow: 1 }}>
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Custom-built solutions to match your exact infrastructure needs"
//                     delay={0.8}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Advanced CI-CD Setup with Security"
//                     delay={0.9}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Kubernetes Infrastructure with 3+ Clusters"
//                     delay={1.0}
//                   />
                  
//                   <AnimatedFeatureItem 
//                     icon={<CheckCircleIcon sx={{ color: '#fad32e', mr: 1.5, fontSize: '1rem', mt: 0.3 }} />}
//                     text="Tightened Security"
//                     delay={1.1}
//                   />
                  
//                   <motion.div
//                     whileHover={{ scale: 1.05 }}
//                     whileTap={{ scale: 0.95 }}
//                     style={{ width: '100%' }}
//                   >
//                     <Button
//                       variant="contained"
//                       sx={{
//                         bgcolor: '#fad32e',
//                         color: '#000000',
//                         py: 1,
//                         px: 4,
//                         width: '100%',
//                         borderRadius: '24px',
//                         '&:hover': {
//                           bgcolor: '#FFFFFF',
//                         },
//                         alignSelf: 'center',
//                         fontWeight: 800,
//                         fontFamily: 'Poppins, sans-serif',
//                         fontSize: '0.9rem'
//                       }}
//                     >
//                       Talk to Sales
//                     </Button>
//                   </motion.div>
//                 </Box>
//               </Box>
//             </AnimatedPricingCard>
//           </Grid>
//         </Grid>
//       </Container>
//     </Box>
//   );
// };

// export default PricingSection;
